import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SERVICES, EVENT_TYPES } from "@/lib/content";
import { trackEvent, getStoredAttribution, getConsent, adsConversion, ADS_LABELS, normalizePhoneE164 } from "@/lib/tracking";
import { useConfig } from "@/components/Layout";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+\d][\d\s()/-]{4,}$/;

export default function LeadForm({ presetService = "", position = "content" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const config = useConfig();
  const [form, setForm] = useState({
    service: presetService, contact_name: "", phone: "", email: "",
    event_type: "", event_date: "", event_date_open: false,
    location_plz: "", guests: "", company: "", message: "",
    consent_marketing: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const submitIdRef = useRef(`sub_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`);
  const startedAtRef = useRef(null);
  const startedTrackedRef = useRef(false);

  useEffect(() => {
    setForm((f) => ({ ...f, service: presetService || f.service }));
  }, [presetService]);

  const markStart = () => {
    if (!startedAtRef.current) {
      startedAtRef.current = new Date().toISOString();
    }
    if (!startedTrackedRef.current) {
      startedTrackedRef.current = true;
      trackEvent("form_start", { page: location.pathname, service: form.service || undefined, position });
    }
  };

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined, general: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.service) errs.service = "Bitte wählen Sie eine Leistung.";
    if (!form.contact_name.trim()) errs.contact_name = "Bitte geben Sie Ihren Namen an.";
    const hasPhone = form.phone.trim() && PHONE_RE.test(form.phone.trim());
    const hasEmail = form.email.trim() && EMAIL_RE.test(form.email.trim());
    if (form.phone.trim() && !hasPhone) errs.phone = "Bitte prüfen Sie die Telefonnummer.";
    if (form.email.trim() && !hasEmail) errs.email = "Bitte prüfen Sie die E-Mail-Adresse.";
    if (!hasPhone && !hasEmail) errs.contact = "Bitte geben Sie Telefon ODER E-Mail an.";
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return; // Schutz vor Doppel-Absendung
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        service: form.service,
        contact_name: form.contact_name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        event_type: form.event_type || null,
        event_date: form.event_date_open ? null : (form.event_date || null),
        event_date_open: form.event_date_open,
        location_plz: form.location_plz.trim() || null,
        guests: form.guests.trim() || null,
        company: form.company.trim() || null,
        message: form.message.trim() || null,
        honeypot: form.website || "",
        client_submit_id: submitIdRef.current,
        attribution: getStoredAttribution(),
        consent_marketing: form.consent_marketing,
        consent_stats: getConsent()?.statistics || false,
        form_started_at: startedAtRef.current,
      };
      const res = await api.post("/leads", payload);
      if (res.data?.ok) {
        if (res.data.conversion_id) {
          const consent = getConsent();
          if (consent?.statistics || consent?.marketing) {
            // Conversion-Event nur mit Einwilligung, mit deduplizierbarer ID.
            trackEvent("generate_lead", {
              conversion_id: res.data.conversion_id,
              service: form.service,
              page: location.pathname,
            });
          }
          // Google Ads Conversion (nur nach erfolgreicher Speicherung, nur mit
          // Marketing-Einwilligung, Enhanced Conversions: Google hasht automatisch)
          adsConversion(ADS_LABELS.form, {
            email: form.email.trim() || undefined,
            phone_number: normalizePhoneE164(form.phone) || undefined,
          });
          trackEvent("form_submit_success", { page: location.pathname, service: form.service, position });
        }
        navigate("/danke");
      } else {
        setErrors({ general: "Die Anfrage konnte nicht gespeichert werden. Bitte versuchen Sie es erneut." });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrors({ general: typeof detail === "string" ? detail : "Speichern fehlgeschlagen. Ihre Eingaben bleiben erhalten – bitte erneut versuchen." });
      toast.error("Anfrage konnte nicht gesendet werden.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full px-4 py-3 bg-[#0e0e11] border border-[#32303c] rounded text-sm text-[#f4efe8] focus:border-[#d97724] focus:outline-none placeholder:text-[#9a958f]";
  const labelCls = "block text-xs uppercase font-mono-brand text-[#a6a29d] mb-1.5";
  const errCls = "mt-1 text-xs text-red-400";

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5" data-testid="lead-form">
      <div>
        <label htmlFor="lf-service" className={labelCls}>Gewünschte Leistung *</label>
        <select id="lf-service" value={form.service} onFocus={markStart} onChange={(e) => set("service", e.target.value)}
          data-testid="lead-form-service" className={inputCls} required aria-invalid={!!errors.service}>
          <option value="" disabled>Bitte wählen</option>
          {SERVICES.map((s) => (
            <option key={s.id} value={s.id}>{s.title}</option>
          ))}
          <option value="noch_unsicher">Noch unsicher – bitte beraten</option>
        </select>
        {errors.service && <p className={errCls} data-testid="lead-form-error-service">{errors.service}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="lf-name" className={labelCls}>Ansprechpartner / Vorname *</label>
          <input id="lf-name" type="text" autoComplete="name" value={form.contact_name} onFocus={markStart}
            onChange={(e) => set("contact_name", e.target.value)} placeholder="Ihr Name" maxLength={120}
            data-testid="lead-form-name" className={inputCls} required aria-invalid={!!errors.contact_name} />
          {errors.contact_name && <p className={errCls} data-testid="lead-form-error-name">{errors.contact_name}</p>}
        </div>
        <div>
          <label htmlFor="lf-company" className={labelCls}>Unternehmen (optional)</label>
          <input id="lf-company" type="text" autoComplete="organization" value={form.company}
            onChange={(e) => set("company", e.target.value)} placeholder="Firma" maxLength={120}
            data-testid="lead-form-company" className={inputCls} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="lf-phone" className={labelCls}>Telefon *</label>
          <input id="lf-phone" type="tel" inputMode="tel" autoComplete="tel" value={form.phone}
            onChange={(e) => set("phone", e.target.value)} placeholder="+49 …" maxLength={40}
            data-testid="lead-form-phone" className={inputCls} aria-invalid={!!errors.phone} />
          {errors.phone && <p className={errCls} data-testid="lead-form-error-phone">{errors.phone}</p>}
        </div>
        <div>
          <label htmlFor="lf-email" className={labelCls}>E-Mail *</label>
          <input id="lf-email" type="email" inputMode="email" autoComplete="email" value={form.email}
            onChange={(e) => set("email", e.target.value)} placeholder="name@firma.de" maxLength={160}
            data-testid="lead-form-email" className={inputCls} aria-invalid={!!errors.email} />
          {errors.email && <p className={errCls} data-testid="lead-form-error-email">{errors.email}</p>}
        </div>
      </div>
      <p className="text-xs text-[#8f8a85] -mt-2">* Mindestens Telefon <em>oder</em> E-Mail ist erforderlich.</p>
      {errors.contact && <p className={errCls} data-testid="lead-form-error-contact">{errors.contact}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="lf-event-type" className={labelCls}>Veranstaltungsart (optional)</label>
          <select id="lf-event-type" value={form.event_type} onChange={(e) => set("event_type", e.target.value)}
            data-testid="lead-form-event-type" className={inputCls}>
            <option value="">Bitte wählen</option>
            {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="lf-date" className={labelCls}>Datum (optional)</label>
          <div className="flex items-center gap-3">
            <input id="lf-date" type="date" value={form.event_date} disabled={form.event_date_open}
              onChange={(e) => set("event_date", e.target.value)}
              data-testid="lead-form-date" className={`${inputCls} disabled:opacity-40`} />
            <label className="flex items-center gap-1.5 text-xs text-[#a6a29d] whitespace-nowrap cursor-pointer">
              <input type="checkbox" className="w-4 h-4 accent-[#d97724]" checked={form.event_date_open}
                data-testid="lead-form-date-open" onChange={(e) => set("event_date_open", e.target.checked)} />
              Noch offen
            </label>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="lf-location" className={labelCls}>Ort / PLZ (optional)</label>
          <input id="lf-location" type="text" autoComplete="postal-code" value={form.location_plz}
            onChange={(e) => set("location_plz", e.target.value)} placeholder="z. B. 69126 Heidelberg" maxLength={80}
            data-testid="lead-form-location" className={inputCls} />
        </div>
        <div>
          <label htmlFor="lf-guests" className={labelCls}>Gästezahl ca. (optional)</label>
          <input id="lf-guests" type="text" inputMode="numeric" value={form.guests}
            onChange={(e) => set("guests", e.target.value)} placeholder="z. B. 80" maxLength={40}
            data-testid="lead-form-guests" className={inputCls} />
        </div>
      </div>

      <div>
        <label htmlFor="lf-message" className={labelCls}>Nachricht (optional)</label>
        <textarea id="lf-message" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)}
          placeholder="Besondere Wünsche, Getränkeideen, Ablauf …" maxLength={2000}
          data-testid="lead-form-message" className={inputCls} />
      </div>

      {/* Honeypot: fuer Menschen unsichtbar */}
      <div className="absolute left-[-9999px] top-[-9999px]" aria-hidden="true">
        <label htmlFor="lf-website">Website</label>
        <input id="lf-website" type="text" tabIndex={-1} autoComplete="off" value={form.website || ""}
          onChange={(e) => set("website", e.target.value)} />
      </div>

      <label className="flex items-start gap-3 cursor-pointer text-xs text-[#a6a29d]">
        <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#d97724]" checked={form.consent_marketing}
          data-testid="lead-form-marketing-consent" onChange={(e) => set("consent_marketing", e.target.checked)} />
        <span>
          <strong className="text-[#f4efe8]">Freiwillig:</strong> Ich stimme zu, dass meine Angaben auch für Werbe-Messung
          (Conversion-Tracking) verwendet werden dürfen. Widerruf jederzeit möglich.
        </span>
      </label>

      <p className="text-xs text-[#8f8a85] leading-relaxed">
        Ihre Angaben verwenden wir zur Bearbeitung Ihrer Anfrage (Art. 6 Abs. 1 lit. b DSGVO). Eine Löschung erfolgt
        spätestens {config?.retention_days || 90} Tage nach Eingang. Details in der{" "}
        <Link to="/datenschutz" className="underline text-[#d97724]" data-testid="lead-form-privacy-link">Datenschutzerklärung</Link>.
      </p>

      {errors.general && (
        <p className="text-sm text-red-400 bg-red-950/40 border border-red-900 rounded px-4 py-3" data-testid="lead-form-error-general" role="alert">
          {errors.general}
        </p>
      )}

      <button type="submit" disabled={submitting} data-testid="lead-form-submit-button"
        className="w-full py-4 min-h-[52px] bg-[#b35c0f] text-white text-sm font-bold uppercase tracking-wider rounded hover:bg-[#96500c] transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
        {submitting ? "Wird gesendet …" : "Kostenloses Angebot anfordern"}
      </button>
      <p className="text-center text-xs text-[#8f8a85] font-mono-brand">Kostenlos & unverbindlich.</p>
    </form>
  );
}
